import { X, Phone, MessageCircle, MapPin, User, Bike, Store, Star, Lock } from "lucide-react";
import { whatsappLink, whatsappMessageFor, normalisePhone } from "@/lib/contactActions";

/**
 * Universal contact card modal — used across Driver / Restaurant dashboards
 * whenever a customer/driver/restaurant contact card is tapped. Renders a
 * hero avatar, phone, address (if given) and three thumb-reach CTAs: Call,
 * WhatsApp, and (optionally) in-app Message.
 *
 * `role` drives the vibe (colour + icon) and the default WhatsApp message.
 *   role: "customer" | "driver" | "restaurant"
 *   direction: matching key in whatsappMessageFor() — decides the pre-filled text.
 *
 * The `contact` shape is deliberately loose so we don't have to reshape
 * everywhere it's used:
 *   { name, phone, photo, subtitle, address, rating, vehicle }
 */
export default function ContactDetailsModal({
  contact,
  role = "customer",
  direction,
  order = {},
  onClose,
  onMessage,
  canChat = false,
  messageDisabledReason,
}) {
  const {
    name = role === "driver" ? "Your driver" : role === "restaurant" ? "Restaurant" : "Customer",
    phone,
    photo,
    subtitle,
    address,
    rating,
    vehicle,
    vehicleDetail,
  } = contact || {};

  const roleTheme = {
    driver: {
      Icon: Bike,
      chip: "bg-eatup-green/10 text-eatup-green border-eatup-green/30",
      header: "from-eatup-green to-eatup-green-dark",
      label: "Driver",
    },
    customer: {
      Icon: User,
      chip: "bg-eatup-charcoal/10 text-eatup-charcoal border-eatup-charcoal/20",
      header: "from-eatup-charcoal to-black",
      label: "Customer",
    },
    restaurant: {
      Icon: Store,
      chip: "bg-eatup-yellow/20 text-amber-700 border-eatup-yellow/50",
      header: "from-eatup-yellow to-amber-500",
      label: "Restaurant",
    },
  }[role] || {
    Icon: User,
    chip: "bg-neutral-100 text-neutral-700 border-neutral-200",
    header: "from-neutral-500 to-neutral-700",
    label: "Contact",
  };
  const RoleIcon = roleTheme.Icon;

  const cleanedPhone = normalisePhone(phone);
  const waMessage = whatsappMessageFor(
    direction || `${role === "driver" ? "customer_to_driver" : role === "customer" ? "driver_to_customer" : "restaurant_to_customer"}`,
    order,
  );
  const waHref = whatsappLink(cleanedPhone, waMessage);

  return (
    <div
      className="fixed inset-0 z-[65] flex items-end sm:items-center justify-center p-3 sm:p-6 fade-up"
      data-testid="contact-details-modal"
      data-role={role}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-[28px] shadow-2xl overflow-hidden text-eatup-charcoal">
        {/* Header strip */}
        <div className={`relative h-24 bg-gradient-to-r ${roleTheme.header}`}>
          <button
            onClick={onClose}
            data-testid="contact-details-close"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/20 backdrop-blur text-white hover:bg-white/30 flex items-center justify-center transition"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Avatar + identity */}
        <div className="-mt-14 px-6 pb-5">
          <div className="flex flex-col items-center">
            {photo ? (
              <img
                src={photo}
                alt={name}
                className="w-24 h-24 rounded-full object-cover ring-4 ring-white shadow-lg"
                data-testid="contact-avatar"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-eatup-charcoal text-white ring-4 ring-white shadow-lg flex items-center justify-center font-display font-black text-3xl">
                {name[0]?.toUpperCase() || <RoleIcon className="w-8 h-8" />}
              </div>
            )}
            <div className="mt-3 font-display font-black text-2xl" data-testid="contact-name">
              {name}
            </div>

            <div className="mt-1 flex items-center gap-2 flex-wrap justify-center">
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-black uppercase tracking-widest text-[10px] ${roleTheme.chip}`}
                data-testid="contact-role-chip"
              >
                <RoleIcon className="w-3 h-3" /> {roleTheme.label}
              </span>
              {rating != null && (
                <span className="inline-flex items-center gap-1 text-eatup-yellow font-bold text-sm">
                  <Star className="w-4 h-4 fill-eatup-yellow" /> {Number(rating).toFixed(1)}
                </span>
              )}
              {vehicle && (
                <span className="text-xs text-neutral-500 font-semibold">· {vehicle}{vehicleDetail ? ` (${vehicleDetail})` : ""}</span>
              )}
            </div>

            {subtitle && (
              <div className="mt-1 text-xs text-neutral-500">{subtitle}</div>
            )}
          </div>

          {/* Info rows */}
          <div className="mt-4 space-y-2">
            {phone && (
              <div className="flex items-center gap-3 rounded-2xl bg-neutral-100 px-4 py-2.5" data-testid="contact-phone-row">
                <Phone className="w-4 h-4 text-eatup-green shrink-0" />
                <div className="text-sm font-semibold flex-1 min-w-0 truncate">{phone}</div>
              </div>
            )}
            {address && (
              <div className="flex items-center gap-3 rounded-2xl bg-neutral-100 px-4 py-2.5" data-testid="contact-address-row">
                <MapPin className="w-4 h-4 text-eatup-green shrink-0" />
                <div className="text-sm font-semibold flex-1 min-w-0 truncate">{address}</div>
              </div>
            )}
          </div>

          {/* Actions — Call · WhatsApp · Message */}
          <div className={`mt-5 grid ${onMessage ? "grid-cols-3" : "grid-cols-2"} gap-2.5`}>
            <a
              href={cleanedPhone ? `tel:${cleanedPhone}` : undefined}
              onClick={(e) => { if (!cleanedPhone) e.preventDefault(); }}
              data-testid="contact-call-btn"
              className={`rounded-2xl py-3.5 flex flex-col items-center gap-1 shadow-lg transition active:scale-[0.98] ${
                cleanedPhone
                  ? "bg-eatup-green hover:bg-eatup-green-dark text-white"
                  : "bg-neutral-200 text-neutral-500 cursor-not-allowed"
              }`}
            >
              <Phone className="w-5 h-5" />
              <span className="font-black uppercase tracking-widest text-[11px]">Call</span>
            </a>

            <a
              href={waHref || undefined}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => { if (!waHref) e.preventDefault(); }}
              data-testid="contact-whatsapp-btn"
              aria-label="Chat on WhatsApp"
              className={`rounded-2xl py-3.5 flex flex-col items-center gap-1 shadow-lg transition active:scale-[0.98] ${
                waHref
                  ? "bg-[#25D366] hover:bg-[#1ebe57] text-white"
                  : "bg-neutral-200 text-neutral-500 cursor-not-allowed"
              }`}
            >
              {/* WhatsApp glyph — brand-accurate, no extra dependency */}
              <svg viewBox="0 0 32 32" className="w-5 h-5 fill-current" aria-hidden="true">
                <path d="M19.11 17.87c-.31-.16-1.83-.9-2.11-1s-.49-.16-.7.16-.8 1-.98 1.22c-.18.21-.36.24-.67.08-.31-.16-1.3-.48-2.48-1.53-.92-.82-1.54-1.83-1.72-2.14-.18-.31-.02-.48.13-.63.14-.14.31-.36.47-.54.16-.18.21-.31.31-.52.1-.21.05-.4-.03-.55-.08-.16-.7-1.69-.96-2.32-.25-.61-.5-.53-.7-.54h-.6c-.21 0-.55.08-.83.4-.28.31-1.09 1.07-1.09 2.6s1.12 3.02 1.27 3.23c.16.21 2.2 3.36 5.34 4.72.75.32 1.33.52 1.78.66.75.24 1.43.21 1.97.13.6-.09 1.83-.75 2.09-1.47.26-.72.26-1.34.18-1.47-.08-.13-.28-.21-.6-.37zM16 3C8.82 3 3 8.82 3 16c0 2.29.6 4.44 1.65 6.31L3 29l6.86-1.62A12.94 12.94 0 0 0 16 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm0 23.63c-2.02 0-3.9-.6-5.47-1.62l-.39-.24-3.79.89.9-3.7-.26-.4A10.6 10.6 0 0 1 5.37 16C5.37 10.14 10.14 5.37 16 5.37S26.63 10.14 26.63 16 21.86 26.63 16 26.63z"/>
              </svg>
              <span className="font-black uppercase tracking-widest text-[11px]">WhatsApp</span>
            </a>

            {onMessage && (
              <button
                onClick={onMessage}
                disabled={!canChat}
                data-testid="contact-message-btn"
                className={`rounded-2xl py-3.5 flex flex-col items-center gap-1 shadow-lg transition active:scale-[0.98] ${
                  canChat
                    ? "bg-eatup-charcoal hover:bg-black text-white"
                    : "bg-neutral-200 text-neutral-500 cursor-not-allowed"
                }`}
              >
                {canChat ? <MessageCircle className="w-5 h-5" /> : <Lock className="w-4 h-4" />}
                <span className="font-black uppercase tracking-widest text-[11px]">Message</span>
              </button>
            )}
          </div>

          {onMessage && !canChat && messageDisabledReason && (
            <div className="mt-3 text-[11px] text-center text-neutral-500 font-semibold">
              {messageDisabledReason}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
